import React from "react";
import theme from "../../data/theme";

/**
 * BoardsElsewhere — "your board is under a different league name."
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 IT ONLY EVER APPEARS ABOVE THE SETUP FORM, and that is the whole point.
 * The Live Board's storage is league-scoped, so opening it with the wrong
 * league active renders a blank setup screen — while a board with picks in it
 * sits one name away. From the operator's chair mid-draft, "my board is gone"
 * and "my board is under a different league" are indistinguishable, and the
 * screen actively suggests the first by inviting you to start a new one.
 *
 * This is the SyncAlarm split again: the only person who can act on it is the
 * one who otherwise never finds out, so it says so unprompted.
 *
 * 🔴 IT OFFERS, IT DOES NOT SWITCH. Changing the active league changes
 * settings, pricing and tier overrides — see loadLeagueFromList. Doing that to
 * somebody who just opened a page, on the strength of a guess about which board
 * they meant, is worse than the confusion it fixes. One button, one human
 * decision.
 *
 * 🔴 SILENT FOR A FIRST-TIME USER. `otherBoardsInProgress` returns [] when no
 * board with a sale in it exists anywhere, and this renders null for an empty
 * list — so somebody who has never made a board sees exactly what they saw
 * before this existed. That is a hard requirement, not a nicety: a recovery
 * notice with nothing to recover is the app inventing a problem.
 * ══════════════════════════════════════════════════════════════════════
 *
 * @param boards   from `otherBoardsInProgress` — [{ league, leagueName, picks }]
 * @param openable league names the app can actually switch to. A board whose
 *                 league has since been deleted is still worth NAMING — that is
 *                 the whole message — but a button that cannot work is worse
 *                 than a sentence, so it degrades to text.
 * @param onOpen   (league) => void. Absent, everything degrades to text.
 * @param onDelete ({league, leagueName, picks}) => void. Ben's ask: two
 *                 near-identical names are what made the wrong board reachable,
 *                 so the ones you do not want can be removed. The caller owns
 *                 the confirmation — see ConfirmDestroy.
 *
 * 🔴 DELETING IS ITS OWN CHOSEN ACTION. The Aug 12 incident was a destructive
 * thing that happened without being chosen; the fix for it must not be another
 * one. Nothing here deletes on open, on switch, or as a side effect of
 * anything — it is a separate button behind a separate confirmation that names
 * the pick count.
 */
export default function BoardsElsewhere({ boards, openable = [], onOpen = null, onDelete = null }) {
  if (!boards || !boards.length) return null;

  const canOpen = (league) => !!onOpen && openable.includes(league);

  return (
    <div data-testid="boards-elsewhere" style={{
      background: "rgba(234,179,8,0.10)", border: "1px solid rgba(234,179,8,0.40)",
      borderRadius: theme.radius.md, padding: "12px 16px", marginBottom: 16,
    }}>
      <div style={{
        fontSize: theme.fontSize.caption, textTransform: "uppercase", letterSpacing: "0.08em",
        color: "#fde68a", fontWeight: theme.fontWeight.bold, marginBottom: 8,
      }}>
        {boards.length === 1 ? "You have a board in progress" : "You have boards in progress"}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {boards.map(b => (
          <div key={b.league} style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            fontSize: theme.fontSize.body, color: theme.colors.text.primary,
          }}>
            <span>
              <strong>{b.leagueName}</strong>
              <span style={{ color: theme.colors.text.muted }}>
                {" · "}{b.picks} {b.picks === 1 ? "pick" : "picks"} entered
              </span>
            </span>
            <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 10 }}>
              {/* 🔴 DELETE IS AVAILABLE EVEN WHEN OPEN IS NOT. A board whose
                  league has been removed from the saved list is the one you are
                  MOST likely to want gone, and it is keyed by name in storage
                  regardless of whether a league object still exists. */}
              {onDelete && (
                <button
                  onClick={() => onDelete(b)}
                  aria-label={`Delete the ${b.leagueName} board, ${b.picks} ${b.picks === 1 ? "pick" : "picks"}`}
                  style={{
                    background: "transparent", border: "none", padding: "6px 2px",
                    color: "#fca5a5", fontFamily: "inherit", fontSize: theme.fontSize.caption,
                    cursor: "pointer", textDecoration: "underline",
                  }}
                >Delete</button>
              )}
              {canOpen(b.league) ? (
                <button
                  onClick={() => onOpen(b.league)}
                  style={{
                    background: theme.colors.brand.gradientBtn,
                    border: "none", borderRadius: theme.radius.md, color: "#fff",
                    fontFamily: "inherit", fontSize: theme.fontSize.caption,
                    fontWeight: theme.fontWeight.bold, padding: "6px 14px", cursor: "pointer",
                  }}
                >Open it</button>
              ) : (
                // The league itself is gone from the saved list, so nothing here
                // can switch to it — but naming it is still most of the value.
                <span style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint }}>
                  switch to this league from the menu above
                </span>
              )}
            </span>
          </div>
        ))}
      </div>

      {/* ⚠️ THIS LINE USED TO READ "Starting a new board below does not touch
          them", WHICH WAS FALSE — and it was printed directly above the button
          that switches you INTO one of these leagues, after which starting a
          board wrote straight over it. That is the Aug 12 data loss, and the
          reassurance was actively pointing the wrong way.

          It is true again now, because startBoard refuses to overwrite a board
          with picks without a confirmation that names the count. The sentence
          says what the guard actually guarantees rather than restating the old
          claim: a board is never erased without being asked about. */}
      <div style={{
        marginTop: 10, fontSize: theme.fontSize.caption, color: theme.colors.text.muted, lineHeight: 1.5,
      }}>
        {`No board is ever erased without asking you first, including ${boards.length === 1 ? "this one" : "these"}.`}
      </div>
    </div>
  );
}
